import { PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { VoceSpesa, AnalisiCondomino, formatCurrency, groupVociByCategoria } from '@/lib/index';

const CHART_COLORS = ['#00B5AD', '#2471A3', '#E67E22', '#27AE60', '#C0392B'];

interface PieChartCategorieProps {
  voci: VoceSpesa[];
}

export function PieChartCategorie({ voci }: PieChartCategorieProps) {
  const macroCategorie = groupVociByCategoria(voci);
  const total = macroCategorie.reduce((sum, item) => sum + item.totale_consuntivo, 0);

  const chartData = macroCategorie.map((item) => ({
    name: item.nome,
    value: item.totale_consuntivo,
    percentage: total > 0 ? (item.totale_consuntivo / total) * 100 : 0,
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground">{payload[0].name}</p>
          <p className="text-sm text-muted-foreground italic">
            {formatCurrency(payload[0].value)}
          </p>
          <p className="text-sm text-primary font-medium">
            {payload[0].payload.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="45%"
          innerRadius={80}
          outerRadius={130}
          fill="#8884d8"
          dataKey="value"
          paddingAngle={2}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="middle"
          align="right"
          layout="vertical"
          iconType="circle"
          formatter={(value) => <span className="text-sm text-foreground">{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

interface BarChartConfrontoProps {
  voci: VoceSpesa[];
}

export function BarChartConfronto({ voci }: BarChartConfrontoProps) {
  const macroCategorie = groupVociByCategoria(voci);

  const chartData = macroCategorie.map((item) => ({
    categoria: item.nome.length > 15 ? item.nome.substring(0, 15) + '...' : item.nome,
    fullName: item.nome,
    consuntivo: item.totale_consuntivo,
    preventivo: item.totale_preventivo,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const fullName = payload[0]?.payload?.fullName || label;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground mb-2">{fullName}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm italic" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis
          dataKey="categoria"
          tick={{ fill: 'oklch(0.48 0.015 220)', fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          tick={{ fill: 'oklch(0.48 0.015 220)', fontSize: 12 }}
          tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: '20px' }}
          formatter={(value) => <span className="text-sm text-foreground">{value}</span>}
        />
        <Bar dataKey="preventivo" name="Preventivo" fill="#2471A3" radius={[8, 8, 0, 0]} />
        <Bar dataKey="consuntivo" name="Consuntivo" fill="#E67E22" radius={[8, 8, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface BarChartScostamentiProps {
  voci: VoceSpesa[];
}

export function BarChartScostamenti({ voci }: BarChartScostamentiProps) {
  const macroCategorie = groupVociByCategoria(voci);

  const chartData = macroCategorie
    .filter((item) => item.totale_preventivo > 0)
    .map((item) => {
      const scostamento = item.totale_consuntivo - item.totale_preventivo;
      const percentuale = (scostamento / item.totale_preventivo) * 100;
      return {
        categoria: item.nome.length > 15 ? item.nome.substring(0, 15) + '...' : item.nome,
        fullName: item.nome,
        scostamento_percentuale: percentuale,
      };
    })
    .sort((a, b) => Math.abs(b.scostamento_percentuale) - Math.abs(a.scostamento_percentuale));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const fullName = payload[0]?.payload?.fullName || label;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground mb-1">{fullName}</p>
          <p className="text-sm font-medium italic" style={{ color: value < 0 ? '#27AE60' : '#C0392B' }}>
            {value.toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground">
            {value < 0 ? 'Risparmio' : 'Sforamento'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 20, right: 30, left: 120, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis
          type="number"
          tick={{ fill: 'oklch(0.48 0.015 220)', fontSize: 12 }}
          tickFormatter={(value) => `${value.toFixed(0)}%`}
        />
        <YAxis
          type="category"
          dataKey="categoria"
          tick={{ fill: 'oklch(0.48 0.015 220)', fontSize: 12 }}
          width={110}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="scostamento_percentuale" name="Scostamento %" radius={[0, 8, 8, 0]}>
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.scostamento_percentuale < 0 ? '#27AE60' : '#C0392B'}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface BarChartSaldoCondominiProps {
  condomini: AnalisiCondomino[];
}

export function BarChartSaldoCondomini({ condomini }: BarChartSaldoCondominiProps) {
  const chartData = condomini.map((item) => ({
    nominativo: item.nominativo.length > 20 ? item.nominativo.substring(0, 20) + '...' : item.nominativo,
    fullName: item.nominativo,
    saldo_finale: item.saldo_finale,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const fullName = payload[0]?.payload?.fullName || label;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground mb-1">{fullName}</p>
          <p className="text-sm font-medium italic" style={{ color: value > 0 ? '#C0392B' : '#27AE60' }}>
            {formatCurrency(value)}
          </p>
          <p className="text-xs text-muted-foreground">
            {value > 0 ? 'A debito' : 'A credito'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis
          dataKey="nominativo"
          tick={{ fill: 'oklch(0.48 0.015 220)', fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          tick={{ fill: 'oklch(0.48 0.015 220)', fontSize: 12 }}
          tickFormatter={(value) => `€${(value / 1000).toFixed(1)}k`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: '20px' }}
          formatter={(value) => <span className="text-sm text-foreground">{value}</span>}
        />
        <Bar dataKey="saldo_finale" name="Saldo Finale" radius={[8, 8, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.saldo_finale > 0 ? '#C0392B' : '#27AE60'}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
